import React, { Component, ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/// React全体のエラーハンドリング用エラーバウンダリ
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  /// エラーが発生した時に状態を更新
  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  /// エラー情報をキャッチしてログに記録
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error Boundary - エラーをキャッチしました:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // プロダクション環境では外部のエラートラッキングサービスに送信
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  /// エラーログを外部サービスに送信（将来の実装用）
  private logErrorToService(error: Error, errorInfo: ErrorInfo) {
    // TODO: 外部のエラートラッキングサービス（Sentry等）に送信
    console.log('エラーログを外部サービスに送信:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  /// ページリロード処理
  private handleReload = () => {
    window.location.reload();
  };

  /// ホームページに戻る処理
  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary__container">
            <div className="error-boundary__content">
              {/* エラーアイコン */}
              <div className="error-boundary__icon">
                <span className="error-emoji">💥</span>
              </div>
              
              {/* エラーメッセージ */}
              <div className="error-boundary__message">
                <h1 className="error-boundary__title">申し訳ございません</h1>
                <p className="error-boundary__description">
                  アプリケーションでエラーが発生しました。
                  <br />
                  ページをリロードするか、ホームページに戻ってお試しください。
                </p>
              </div>
              
              {/* アクションボタン */}
              <div className="error-boundary__actions">
                <button 
                  onClick={this.handleReload}
                  className="error-boundary__button primary"
                >
                  ページをリロード
                </button>
                
                <button 
                  onClick={this.handleGoHome}
                  className="error-boundary__button secondary"
                >
                  ホームページに戻る
                </button>
              </div>
              
              {/* 開発環境でのエラー詳細 */}
              {process.env.NODE_ENV === 'development' && (
                <details className="error-boundary__details">
                  <summary className="error-boundary__details-summary">
                    エラー詳細（開発環境のみ表示）
                  </summary>
                  <div className="error-boundary__details-content">
                    {this.state.error && (
                      <div className="error-detail">
                        <h3>エラーメッセージ:</h3>
                        <pre>{this.state.error.message}</pre>
                      </div>
                    )}
                    
                    {this.state.error && this.state.error.stack && (
                      <div className="error-detail">
                        <h3>スタックトレース:</h3>
                        <pre>{this.state.error.stack}</pre>
                      </div>
                    )}
                    
                    {this.state.errorInfo && this.state.errorInfo.componentStack && (
                      <div className="error-detail">
                        <h3>コンポーネントスタック:</h3>
                        <pre>{this.state.errorInfo.componentStack}</pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
              
              {/* フィードバック */}
              <div className="error-boundary__feedback">
                <p className="feedback-text">
                  このエラーが続く場合は、お使いのブラウザのキャッシュをクリアしてみてください。
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 